import React from 'react';
import { Box, Button, CircularProgress, Drawer, IconButton, Tooltip, Typography } from '@mui/material';
import { ExternalLink, HelpCircle } from 'lucide-react';

const isPdfName = (name) => String(name || '').toLowerCase().endsWith('.pdf');

const isImageName = (name) => {
  const lower = String(name || '').toLowerCase();
  return (
    lower.endsWith('.jpg') ||
    lower.endsWith('.jpeg') ||
    lower.endsWith('.png') ||
    lower.endsWith('.gif') ||
    lower.endsWith('.webp')
  );
};

export default function DocumentPreviewDrawer({
  open,
  onClose,
  fileName,
  fileUrl,
  loading,
  details = [],
  onOpenInNewTab,
  openInNewTabLoading,
  onDownload,
  downloadDisabled
}) {
  const canPreview = Boolean(fileUrl) && (isPdfName(fileName) || isImageName(fileName));
  const isPdf = Boolean(fileUrl) && isPdfName(fileName);
  const isImage = Boolean(fileUrl) && isImageName(fileName);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      hideBackdrop
      sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
      PaperProps={{
        sx: {
          width: { xs: '100vw', sm: 480 },
          maxWidth: '100vw',
          borderLeft: '1px solid rgba(15, 23, 42, 0.08)',
          boxShadow: '0 0 0 1px rgba(15, 23, 42, 0.06), 0 24px 48px rgba(15, 23, 42, 0.24)'
        }
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            p: 2,
            borderBottom: '1px solid rgba(145, 158, 171, 0.24)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
              Anteprima documento
            </Typography>
            <Tooltip arrow placement="bottom" title="Supporta PDF e immagini. Usa “Apri in nuova scheda” per aprire a schermo intero.">
              <IconButton size="small" sx={{ p: 0.25 }}>
                <HelpCircle size={16} />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {onDownload ? (
              <Button
                size="small"
                variant="outlined"
                onClick={onDownload}
                disabled={downloadDisabled}
                sx={{
                  height: 40,
                  minHeight: 40,
                  px: 2,
                  borderRadius: '12px',
                  fontWeight: 800,
                  textTransform: 'none'
                }}
              >
                Scarica
              </Button>
            ) : null}
            <Button
              size="small"
              variant="outlined"
              startIcon={<ExternalLink size={14} />}
              onClick={onOpenInNewTab}
              disabled={!fileName || !onOpenInNewTab || openInNewTabLoading}
              sx={{
                height: 40,
                minHeight: 40,
                px: 2,
                borderRadius: '12px',
                fontWeight: 800,
                textTransform: 'none'
              }}
            >
              {openInNewTabLoading ? 'Apertura...' : 'Apri in nuova scheda'}
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={onClose}
              sx={{ height: 40, minHeight: 40, px: 1.5, borderRadius: '12px', fontWeight: 800, textTransform: 'none' }}
            >
              Chiudi
            </Button>
          </Box>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ minHeight: 0, flex: 1, overflow: 'auto' }}>
            {loading ? (
              <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            ) : canPreview ? (
              isPdf ? (
                <Box sx={{ height: { xs: '55vh', sm: '68vh' }, maxHeight: '78vh' }}>
                  <iframe title={fileName || 'Anteprima documento'} src={fileUrl} style={{ border: 0, width: '100%', height: '100%' }} />
                </Box>
              ) : isImage ? (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <img src={fileUrl} alt={fileName || 'Documento'} style={{ maxWidth: '100%', maxHeight: '72vh', borderRadius: 8 }} />
                </Box>
              ) : null
            ) : (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Anteprima non disponibile per questo tipo di file.
                </Typography>
              </Box>
            )}
          </Box>

          {details && details.length > 0 ? (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Dettagli
              </Typography>
              {details.map((d) => (
                <Typography key={d.label} variant="body2">
                  {d.label}: {d.value || '-'}
                </Typography>
              ))}
            </Box>
          ) : null}
        </Box>
      </Box>
    </Drawer>
  );
}

